import React, { useRef, useState } from 'react';
import { alpha } from '@mui/material/styles';
import SaveIcon from '@/components/icons/SaveIcon';
import { createConfigurationSchema } from '@/lib/validation/configurations';
import {
  Box,
  Button,
  CircularProgress,
  darken,
  Divider,
  Fab,
  Grid,
  InputLabel,
  Link,
  Menu,
  MenuItem,
  Paper,
  styled,
  Typography,
  Autocomplete,
  Chip,
} from '@mui/material';
import { Field, FieldArray, Formik, FormikHelpers, FormikProps } from 'formik';
import {
  CurrentFiles,
  FileDropZone,
  PTextField,
  SnackBarContext,
} from 'pragmatic-ui';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { MenuProps } from '@mui/material/Menu';
import { useNavigate, useParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import {
  useConfigurations,
  useUpdateConfiguration,
} from '@/hooks/admin/useConfigurations';
import { useContext } from 'react';
import { v4 as uuidV4 } from 'uuid';
import { useCompanies } from '@/hooks/admin/useCompanies';
import { TYPE_OPTIONS } from '@/constants';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DatePickerIcon from '@/components/icons/DatePickerIcon';
import { isDateExpired, isDateExpiring } from '@/lib/dates';
import { useNZTA } from '@/hooks/admin/useNzta';
import {
  PresignedFileInfo,
  PresignRequestFile,
  presignUploads,
  UpdateConfigurationPayload,
} from '@/hooks/admin/usePreSignedUrls';

const StyledForm = styled('form')(() => ({
  width: '100%',
}));

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'left',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'left',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 140,
    color:
      theme.palette.mode === 'light'
        ? 'rgb(55, 65, 81)'
        : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '8px 0',
      margin: '0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity,
        ),
      },
    },
  },
}));

const FileRender = ({
  file,
  onRemoveFileClick,
}: {
  file: CurrentFiles;
  onStarClick: () => void;
  onRemoveFileClick: () => void;
  removeEntry?: boolean;
}) => {
  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        justifyItems="flex-start"
        sx={{
          windth: '325px',
          minWidth: '325px',
        }}
      >
        <Typography
          variant="caption"
          color="primary"
          sx={{
            color: 'primary',
            textAlign: 'left',
          }}
        >
          {file.filename}
        </Typography>
        <Link
          onClick={onRemoveFileClick}
          sx={{
            color: 'primary',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            mr: 12,
          }}
        >
          Remove
        </Link>
      </Box>
    </>
  );
};

export default function EditConfigurationForm() {
  const formikRef = useRef<FormikProps<any>>(null);
  const fileUploadButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const { configurationId } = useParams();
  const navigate = useNavigate();
  const { data: configurations = [], isLoading: isConfigurationsLoading } =
    useConfigurations();
  const updateConfiguration = useUpdateConfiguration();
  const { data: nztaData, isLoading: isNztaLoading } = useNZTA();
  const { showSnack } = useContext(SnackBarContext);
  const { data: _, isLoading: isCompaniesLoading } = useCompanies();
  const [anchorMoreMenu, setAnchorMoreMenu] =
    React.useState<null | HTMLElement>(null);
  const openMoreMenu = Boolean(anchorMoreMenu);
  const [currentRow, setCurrentRow] = React.useState(0);
  const onClickMoreMenu = (
    event: React.MouseEvent<HTMLElement>,
    idx: number,
  ) => {
    setAnchorMoreMenu(event.currentTarget);
    setCurrentRow(idx);
  };
  const handleCloseMenu = () => {
    setAnchorMoreMenu(null);
  };

  const [fileFormatType, setFileFormatType] = useState(0);
  const fileFormatAll = {
    'application/pdf': ['.pdf'],
    'application/geo+json': ['.geojson'], // Add GeoJSON support
    'application/json': ['.geojson', '.json'], // Fallback to JSON MIME type
  };
  const fileFormatPdf = {
    'application/pdf': ['.pdf'],
  };
  const fileFormatJson = {
    'application/geo+json': ['.geojson'], // Add GeoJSON support
    'application/json': ['.geojson', '.json'], // Fallback to JSON MIME type
  };

  const isLoading =
    isConfigurationsLoading || isCompaniesLoading || isNztaLoading;
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  const configuration = configurations.find(
    (configuration) => configuration._id === configurationId,
  );

  const permits = configuration?.permits.map((permit) => {
    return {
      ...permit,
      issueDate: new Date(permit.issueDate),
      expiryDate: new Date(permit.expiryDate),
    };
  });

  const handleSubmit = async (
    formValues: any,
    { resetForm }: FormikHelpers<any>,
  ) => {
    const configurationId = configuration?._id;
    if (!configurationId) {
      showSnack('Configuration ID is missing', 'error');
      return;
    }

    const processedForms = formValues.forms.map((form: any) => ({
      ...form,
      permits: form.permits.map((permit: any) => {
        if (permit.councils && permit.councils.length > 0) {
          const firstCouncil = permit.councils[0];
          if (firstCouncil.parentNztaId) {
            return {
              ...permit,
              rca: firstCouncil.parentNztaId,
            };
          }
        }
        return permit;
      }),
    }));

    type NewFileDescriptor = {
      fileid: string;
      file: File;
      permitIndex: number;
    };

    const newFiles: NewFileDescriptor[] = [];

    processedForms[0].permits.forEach((permit: any, uploadIndex: number) => {
      permit.fileids = permit.fileids || [];

      if (permit.uploads && permit.uploads.length) {
        permit.uploads.forEach((upload: any) => {
          if (
            upload.file &&
            upload.file.uuid &&
            !permit.fileids.includes(upload.file.uuid)
          ) {
            permit.fileids.push(upload.file.uuid);
          }
        });
      }

      const fileKey = `files-${uploadIndex}`;
      const filesForPermit: File[] = formValues[fileKey] || [];

      filesForPermit.forEach((file: File) => {
        const fileid = uuidV4();

        if (!permit.fileids.includes(fileid)) {
          permit.fileids.push(fileid);
        }

        newFiles.push({
          fileid,
          file,
          permitIndex: uploadIndex,
        });
      });
    });

    const modifiedPermits = processedForms[0].permits.map((permit: any) => {
      const base: any = {
        ...(permit._id ? { _id: permit._id } : {}),
        rca: permit.rca,
        type: permit.type,
        localCouncil: permit.localCouncil,
        issueDate: permit.issueDate,
        expiryDate: permit.expiryDate,
        fileids: permit.fileids || [],
        councils:
          permit.councils?.map((council: any) => ({ _id: council._id })) || [],
      };

      if (Array.isArray(permit.uploads)) {
        // will be [] if "Remove All" was clicked
        base.uploads = permit.uploads.map((u: any) => ({
          file: {
            uid: u.file?.uid,
            path: u.file?.path,
            filename: u.file?.filename,
          },
        }));
      }

      return base;
    });

    try {
      let filesMeta: Record<string, any> = {};

      if (newFiles.length > 0) {
        const presignRequestFiles: PresignRequestFile[] = newFiles.map(
          ({ fileid, file }) => ({
            fileid,
            filename: file.name,
            size: file.size,
            mimetype: file.type || 'application/octet-stream',
          }),
        );

        const presigned: PresignedFileInfo[] =
          await presignUploads(presignRequestFiles);

        const presignedById: Record<string, PresignedFileInfo> = {};
        presigned.forEach((info) => {
          presignedById[info.fileid] = info;
        });

        await Promise.all(
          newFiles.map(async ({ fileid, file }) => {
            const info = presignedById[fileid];
            if (!info) {
              throw new Error(`Missing presigned info for fileid ${fileid}`);
            }

            const resp = await fetch(info.uploadUrl, {
              method: 'PUT',
              headers: {
                'Content-Type': info.mimetype,
              },
              body: file,
            });

            if (!resp.ok) {
              throw new Error(
                `Upload failed for ${info.filename} (status ${resp.status})`,
              );
            }
          }),
        );

        filesMeta = presigned.reduce<Record<string, any>>((acc, info) => {
          acc[info.fileid] = {
            bucket: info.bucket,
            key: info.key,
            filename: info.filename,
            size: info.size,
            mimetype: info.mimetype,
          };
          return acc;
        }, {});
      }

      const payload: UpdateConfigurationPayload = {
        _id: configurationId,
        vehicle: formValues.vehicle,
        name: formValues.name,
        multiUnit: formValues.multiUnit,
        uploads: JSON.stringify(modifiedPermits),
        files: filesMeta, // may be {} if no new files
      };

      await updateConfiguration.mutateAsync(payload);

      resetForm();
      showSnack('Configuration update submitted successfully', 'success');
      navigate(`/admin/vehicles/${configuration?.vehicle}`);
    } catch (error: any) {
      console.error('Error updating configuration:', error);
      showSnack(
        error?.message || 'Error updating configuration. Please try again.',
        'error',
      );
    }
  };

  const onClickRemoveRow = () => {
    if (formikRef.current) {
      handleCloseMenu();
      const { values, setFieldValue } = formikRef.current;
      const updatedPermits = values.forms[0].permits.filter(
        (_: any, idx: number) => idx !== currentRow,
      );
      setFieldValue(`forms.${0}.permits`, updatedPermits);
    }
  };

  const onClickRemoveAll = () => {
    if (formikRef.current) {
      formikRef.current.setFieldValue(
        `forms.${0}.permits.${currentRow}.uploads`,
        [],
      );
      const { values, setFieldValue } = formikRef.current;
      if (values[`files-${currentRow}`]) {
        setFieldValue(`files-${currentRow}`, undefined);
      }
    }
    handleCloseMenu();
  };

  const onClickUploadAll = () => {
    setFileFormatType(0);
    setTimeout(() => {
      if (formikRef.current && fileUploadButtonRefs.current[currentRow]) {
        fileUploadButtonRefs.current[currentRow].click();
      }
    }, 300);
    handleCloseMenu();
  };

  const onClickUploadPermit = () => {
    setFileFormatType(1);
    //const { values } = formikRef.current;
    setTimeout(() => {
      if (formikRef.current && fileUploadButtonRefs.current[currentRow]) {
        fileUploadButtonRefs.current[currentRow].click();
        setFileFormatType(0);
      }
    }, 300);
    handleCloseMenu();
  };

  const onClickUploadJson = () => {
    setFileFormatType(2);
    //const { values } = formikRef.current;
    setTimeout(() => {
      if (formikRef.current && fileUploadButtonRefs.current[currentRow]) {
        fileUploadButtonRefs.current[currentRow].click();
        setFileFormatType(0);
      }
    }, 300);
    handleCloseMenu();
  };

  const onClickRemovePermit = () => {
    if (formikRef.current) {
      const { values, setFieldValue } = formikRef.current;
      const updatedUploads = values.forms[0].permits[currentRow].uploads.filter(
        (u: { file: { filename: string } }) => {
          const isPdf = u.file.filename.endsWith('.pdf');
          return !isPdf;
        },
      );
      setFieldValue(`forms.${0}.permits.${currentRow}.uploads`, updatedUploads);
    }
    handleCloseMenu();
  };

  const onClickRemoveJson = () => {
    if (formikRef.current) {
      const { values, setFieldValue } = formikRef.current;
      const updatedUploads = values.forms[0].permits[currentRow].uploads.filter(
        (u: { file: { filename: string } }) => {
          const isJson =
            u.file.filename.endsWith('.json') ||
            u.file.filename.endsWith('.geojson');
          return !isJson;
        },
      );
      setFieldValue(`forms.${0}.permits.${currentRow}.uploads`, updatedUploads);
    }
    handleCloseMenu();
  };

  const formikProps = {
    initialValues: {
      vehicle: configuration?.vehicle,
      name: configuration?.name,
      multiUnit: configuration?.multiUnit,
      forms: [
        {
          permits:
            permits !== undefined && permits.length > 0
              ? permits.map((permit) => ({
                  ...permit,
                  // Determine localCouncil based on existing data
                  localCouncil: permit?.localCouncil || false,
                }))
              : [
                  {
                    rca: '',
                    councils: [],
                    type: '',
                    issueDate: new Date(),
                    expiryDate: new Date(),
                    //uploads:[{ file: {} }],
                  },
                ],
        },
      ],
    },
    validationSchema: createConfigurationSchema,
    onSubmit: handleSubmit,
  };

  const getAllCouncils = () => {
    if (!nztaData) return [];
    return nztaData.reduce((allCouncils: any, nzta: any) => {
      const councilsWithParent = (nzta.councils || []).map((council: any) => ({
        ...council,
        parentNztaId: nzta._id,
      }));
      return [...allCouncils, ...councilsWithParent];
    }, []);
  };

  return (
    <Box px={4}>
      <Formik innerRef={formikRef} {...formikProps}>
        {({ handleSubmit, setFieldValue, values }: FormikProps<any>) => {
          return (
            <>
              <StyledForm onSubmit={handleSubmit}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box display={'flex'} alignItems="center">
                    <PTextField
                      variant="standard"
                      autoComplete="off"
                      name={'name'}
                      placeholder="Configuration Name"
                      InputProps={{
                        disableUnderline: false,
                      }}
                      sx={{
                        width: '300px',
                      }}
                    />
                  </Box>
                  <Box display="flex" alignItems="center">
                    <Button
                      size="small"
                      sx={{
                        textTransform: 'uppercase',
                        fontWeight: '700',
                        mr: 2,
                      }}
                      onClick={() => navigate(-1)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      size="small"
                      color="primary"
                      startIcon={<SaveIcon />}
                      sx={{ textTransform: 'uppercase', fontWeight: '700' }}
                    >
                      Save
                    </Button>
                  </Box>
                </Box>
                <>
                  <FieldArray name="forms">
                    {() => (
                      <>
                        {values.forms.map((form: any, index: number) => (
                          <Box py={4} key={`form-${index}`}>
                            <Grid container columnSpacing={8}>
                              <FieldArray name={`forms.${index}.permits`}>
                                {({
                                  remove: removePermit,
                                  push: pushPermit,
                                }: any) => (
                                  <>
                                    <Grid
                                      item
                                      xs={9}
                                      mt={8}
                                      key={`permits-${index}`}
                                    >
                                      <Typography
                                        variant="h6"
                                        sx={{ color: '#EAEAEA' }}
                                      >
                                        Attach Permits
                                      </Typography>
                                      <Paper
                                        component="div"
                                        sx={{
                                          py: 3,
                                          pl: 2,
                                          pr: 0,
                                          mt: 4,
                                          borderRadius: '16px',
                                        }}
                                      >
                                        {form.permits.map(
                                          (permit: any, idx: number) => (
                                            <Grid
                                              container
                                              columnSpacing={4}
                                              mt={idx > 0 ? 0 : 0}
                                              key={`permit-${index}-${idx}`}
                                            >
                                              {/* <Grid item xs={2}>
                                                <Field
                                                  name={`forms.${index}.permits.${idx}.rca`}
                                                >
                                                  {({ field }: any) => (
                                                    <PTextField
                                                      {...field}
                                                      label={idx == 0 && 'RCA'}
                                                      placeholder="RCA"
                                                      fullWidth
                                                    />
                                                  )}
                                                </Field>
                                              </Grid> */}
                                              <Grid item xs={2}>
                                                <Field
                                                  name={`forms.${index}.permits.${idx}.localCouncil`}
                                                >
                                                  {({ field, form }: any) => (
                                                    <PTextField
                                                      {...field}
                                                      label={
                                                        idx === 0 ? 'RCA' : ''
                                                      }
                                                      placeholder="RCA"
                                                      fullWidth
                                                      select
                                                      value={
                                                        field.value
                                                          ? 'localCouncil'
                                                          : 'nzta'
                                                      }
                                                      onChange={(e) => {
                                                        const isLocalCouncil =
                                                          e.target.value ===
                                                          'localCouncil';
                                                        form.setFieldValue(
                                                          `forms.${index}.permits.${idx}.localCouncil`,
                                                          isLocalCouncil,
                                                        );

                                                        // Clear NZTA field when switching to local council
                                                        if (isLocalCouncil) {
                                                          form.setFieldValue(
                                                            `forms.${index}.permits.${idx}.rca`,
                                                            '',
                                                          );
                                                        }

                                                        // Clear councils when switching modes
                                                        form.setFieldValue(
                                                          `forms.${index}.permits.${idx}.councils`,
                                                          [],
                                                        );
                                                      }}
                                                    >
                                                      <MenuItem value="nzta">
                                                        NZTA
                                                      </MenuItem>
                                                      <MenuItem value="localCouncil">
                                                        Local council
                                                      </MenuItem>
                                                    </PTextField>
                                                  )}
                                                </Field>
                                              </Grid>
                                              <Grid item xs={2}>
                                                <Field
                                                  name={`forms.${index}.permits.${idx}.rca`}
                                                >
                                                  {({ field, form }: any) => {
                                                    const isLocalCouncil =
                                                      form.values.forms[index]
                                                        .permits[idx]
                                                        .localCouncil;

                                                    return (
                                                      <Autocomplete
                                                        options={nztaData || []}
                                                        getOptionLabel={(
                                                          option,
                                                        ) => option.name}
                                                        value={
                                                          isLocalCouncil
                                                            ? null
                                                            : nztaData?.find(
                                                                (nzta) =>
                                                                  nzta._id ===
                                                                  field.value,
                                                              ) || null
                                                        }
                                                        onChange={(
                                                          _,
                                                          value,
                                                        ) => {
                                                          form.setFieldValue(
                                                            `forms.${index}.permits.${idx}.rca`,
                                                            value?._id || '',
                                                          );
                                                        }}
                                                        disabled={
                                                          isLocalCouncil
                                                        } // Disable when local council is selected
                                                        renderInput={(
                                                          params,
                                                        ) => (
                                                          <PTextField
                                                            {...params}
                                                            {...field}
                                                            value={
                                                              isLocalCouncil
                                                                ? ''
                                                                : field.value
                                                            }
                                                            label={
                                                              idx === 0
                                                                ? 'NZTA'
                                                                : ''
                                                            }
                                                            placeholder="NZTA"
                                                            fullWidth
                                                          />
                                                        )}
                                                      />
                                                    );
                                                  }}
                                                </Field>
                                              </Grid>

                                              <Grid item xs={3}>
                                                <Field
                                                  name={`forms.${index}.permits.${idx}.councils`}
                                                >
                                                  {({ field, form }: any) => {
                                                    const isLocalCouncil =
                                                      form.values.forms[index]
                                                        .permits[idx]
                                                        .localCouncil;
                                                    const selectedRcaId =
                                                      form.values.forms[index]
                                                        .permits[idx].rca;

                                                    let availableCouncils: any =
                                                      [];
                                                    let existingCouncils = [];

                                                    if (isLocalCouncil) {
                                                      availableCouncils =
                                                        getAllCouncils();

                                                      if (
                                                        Array.isArray(
                                                          field.value,
                                                        )
                                                      ) {
                                                        existingCouncils =
                                                          availableCouncils.filter(
                                                            (c: any) =>
                                                              field.value.find(
                                                                (f: any) =>
                                                                  f._id ===
                                                                  c._id,
                                                              ),
                                                          );
                                                      } else {
                                                        existingCouncils = [];
                                                      }
                                                    } else {
                                                      const selectedRca =
                                                        nztaData?.find(
                                                          (nzta) =>
                                                            nzta._id ===
                                                            selectedRcaId,
                                                        );
                                                      availableCouncils =
                                                        selectedRca?.councils ||
                                                        [];

                                                      existingCouncils =
                                                        selectedRca
                                                          ? selectedRca.councils.filter(
                                                              (c) =>
                                                                Array.isArray(
                                                                  field.value,
                                                                )
                                                                  ? field.value.find(
                                                                      (
                                                                        f: any,
                                                                      ) =>
                                                                        f._id ===
                                                                        c._id,
                                                                    )
                                                                  : false,
                                                            )
                                                          : [];
                                                    }

                                                    return (
                                                      <Autocomplete
                                                        multiple
                                                        options={
                                                          availableCouncils
                                                        }
                                                        getOptionLabel={(
                                                          option,
                                                        ) => option.name}
                                                        isOptionEqualToValue={(
                                                          option,
                                                          value,
                                                        ) =>
                                                          option._id ===
                                                          value._id
                                                        }
                                                        value={existingCouncils}
                                                        onChange={(
                                                          _,
                                                          newValue,
                                                        ) => {
                                                          form.setFieldValue(
                                                            `forms.${index}.permits.${idx}.councils`,
                                                            newValue,
                                                          );
                                                        }}
                                                        renderInput={(
                                                          params,
                                                        ) => (
                                                          <PTextField
                                                            {...params}
                                                            {...field}
                                                            label={
                                                              idx === 0
                                                                ? 'COUNCILS'
                                                                : ''
                                                            }
                                                            placeholder="Select Councils"
                                                            fullWidth
                                                          />
                                                        )}
                                                        renderTags={(
                                                          tagValue,
                                                          getTagProps,
                                                        ) =>
                                                          tagValue.map(
                                                            (option, index) => (
                                                              <Chip
                                                                label={
                                                                  option.name
                                                                }
                                                                {...getTagProps(
                                                                  { index },
                                                                )}
                                                                key={option._id}
                                                              />
                                                            ),
                                                          )
                                                        }
                                                        disabled={
                                                          !isLocalCouncil &&
                                                          !selectedRcaId
                                                        }
                                                      />
                                                    );
                                                  }}
                                                </Field>
                                              </Grid>

                                              <Grid item xs={2}>
                                                <Field
                                                  name={`forms.${index}.permits.${idx}.type`}
                                                >
                                                  {({ field }: any) => (
                                                    <PTextField
                                                      {...field}
                                                      label={idx == 0 && 'TYPE'}
                                                      placeholder="Type"
                                                      fullWidth
                                                      select
                                                    >
                                                      {TYPE_OPTIONS.map(
                                                        (type) => (
                                                          <MenuItem
                                                            key={`type-${type}`}
                                                            value={type}
                                                          >
                                                            {type}
                                                          </MenuItem>
                                                        ),
                                                      )}
                                                    </PTextField>
                                                  )}
                                                </Field>
                                              </Grid>

                                              <Grid item xs={2}>
                                                <Field
                                                  name={`forms.${index}.permits.${idx}.issueDate`}
                                                >
                                                  {({
                                                    field,
                                                  }: {
                                                    field: {
                                                      name: string;
                                                      value: Date;
                                                      onChange: (
                                                        event: React.ChangeEvent<HTMLInputElement>,
                                                      ) => void;
                                                      onBlur: (
                                                        event: React.FocusEvent<HTMLInputElement>,
                                                      ) => void;
                                                    };
                                                  }) => (
                                                    <LocalizationProvider
                                                      dateAdapter={
                                                        AdapterDateFns
                                                      }
                                                    >
                                                      <DesktopDatePicker
                                                        name={`forms.${index}.permits.${idx}.issueDate`}
                                                        value={field.value}
                                                        onChange={(date) =>
                                                          setFieldValue(
                                                            field.name,
                                                            date,
                                                          )
                                                        }
                                                        label={
                                                          idx == 0 && 'ISSUED'
                                                        }
                                                        format="dd/MM/yyyy"
                                                        slots={{
                                                          openPickerIcon:
                                                            DatePickerIcon,
                                                        }}
                                                      />
                                                    </LocalizationProvider>
                                                  )}
                                                </Field>
                                              </Grid>
                                              <Grid item xs={2}>
                                                <Field
                                                  name={`forms.${index}.permits.${idx}.expiryDate`}
                                                >
                                                  {({
                                                    field,
                                                  }: {
                                                    field: {
                                                      name: string;
                                                      value: Date;
                                                      onChange: (
                                                        event: React.ChangeEvent<HTMLInputElement>,
                                                      ) => void;
                                                      onBlur: (
                                                        event: React.FocusEvent<HTMLInputElement>,
                                                      ) => void;
                                                    };
                                                  }) => (
                                                    <LocalizationProvider
                                                      dateAdapter={
                                                        AdapterDateFns
                                                      }
                                                    >
                                                      <DesktopDatePicker
                                                        name={`forms.${index}.permits.${idx}.expiryDate`}
                                                        value={field.value}
                                                        onChange={(date) =>
                                                          setFieldValue(
                                                            field.name,
                                                            date,
                                                          )
                                                        }
                                                        label={
                                                          idx == 0 && 'EXPIRY'
                                                        }
                                                        format="dd/MM/yyyy"
                                                        slots={{
                                                          openPickerIcon:
                                                            DatePickerIcon,
                                                        }}
                                                        sx={{
                                                          '& .MuiInputBase-root':
                                                            {
                                                              backgroundColor:
                                                                isDateExpired(
                                                                  field.value,
                                                                )
                                                                  ? '#f44336 !important'
                                                                  : isDateExpiring(
                                                                        field.value,
                                                                      )
                                                                    ? '#906a2f !important'
                                                                    : '',
                                                            },
                                                        }}
                                                      />
                                                    </LocalizationProvider>
                                                  )}
                                                </Field>
                                              </Grid>

                                              <Grid item xs={3}>
                                                <InputLabel
                                                  sx={{
                                                    color: '#fff',
                                                  }}
                                                >
                                                  {idx == 0 && 'UPLOADS'}
                                                </InputLabel>

                                                <FileDropZone
                                                  name={`files-${idx}`}
                                                  maxFiles={20}
                                                  featured
                                                  fileFormat={
                                                    fileFormatType === 0
                                                      ? fileFormatAll
                                                      : fileFormatType === 1
                                                        ? fileFormatPdf
                                                        : fileFormatJson
                                                  }
                                                  renderTitle={(
                                                    openFilePicker,
                                                  ) =>
                                                    permit.uploads &&
                                                    permit.uploads.length >
                                                      0 ? (
                                                      permit.uploads.map(
                                                        (
                                                          u: any,
                                                          fileIdx: number,
                                                        ) => {
                                                          const { file } = u;
                                                          return (
                                                            <Grid
                                                              item
                                                              xs={6}
                                                              key={
                                                                file.uuid ||
                                                                `form-files-${idx}-${fileIdx}`
                                                              }
                                                            >
                                                              <Link
                                                                target="_blank"
                                                                href={
                                                                  file?.signedUrl
                                                                }
                                                                download={
                                                                  file.filename
                                                                }
                                                                sx={{
                                                                  color:
                                                                    'primary',
                                                                  mt: 2,
                                                                  display:
                                                                    'flex',
                                                                  justifyContent:
                                                                    'flex-start',
                                                                  alignItems:
                                                                    'center',
                                                                  textAlign:
                                                                    'left',
                                                                  width: '100%',
                                                                }}
                                                              >
                                                                {file.filename}
                                                              </Link>
                                                              {/*  */}
                                                            </Grid>
                                                          );
                                                        },
                                                      )
                                                    ) : (
                                                      <>
                                                        <Typography
                                                          variant="caption"
                                                          sx={{
                                                            fontSize: '14px',
                                                            ml: -7,
                                                          }}
                                                          onClick={() => {
                                                            setFileFormatType(
                                                              0,
                                                            );
                                                            setTimeout(() => {
                                                              if (
                                                                openFilePicker
                                                              ) {
                                                                openFilePicker();
                                                              }
                                                            }, 500);
                                                          }}
                                                        >
                                                          Drag and drop to
                                                          upload (max file size:
                                                          50 MB)
                                                        </Typography>
                                                      </>
                                                    )
                                                  }
                                                  renderButton={(
                                                    openFilePicker,
                                                  ) => (
                                                    <>
                                                      <Button
                                                        ref={(el) =>
                                                          (fileUploadButtonRefs.current[
                                                            idx
                                                          ] = el)
                                                        }
                                                        variant="contained"
                                                        color="primary"
                                                        sx={{
                                                          display: 'none',
                                                          mt: 2,
                                                          textTransform:
                                                            'uppercase',
                                                        }}
                                                        onClick={openFilePicker}
                                                      >
                                                        Upload
                                                      </Button>
                                                    </>
                                                  )}
                                                  renderFile={(
                                                    newFile,
                                                    onStarClick,
                                                    onRemoveFileClick,
                                                  ) => {
                                                    return (
                                                      <FileRender
                                                        file={newFile}
                                                        onStarClick={
                                                          onStarClick
                                                        }
                                                        onRemoveFileClick={
                                                          onRemoveFileClick
                                                        }
                                                      />
                                                    );
                                                  }}
                                                  containerSx={{
                                                    background: 'transparent',
                                                    px: 0,
                                                    mx: 0,
                                                  }}
                                                />
                                              </Grid>
                                              <Grid
                                                item
                                                xs={1}
                                                display="flex"
                                                justifyContent="center"
                                                alignItems="center"
                                              >
                                                {permit._id && (
                                                  <Fab
                                                    onClick={(event) => {
                                                      onClickMoreMenu(
                                                        event,
                                                        idx,
                                                      );
                                                    }}
                                                    color="primary"
                                                    size="small"
                                                    sx={{
                                                      mr: 2,
                                                      '&:hover': {
                                                        backgroundColor: darken(
                                                          '#F6AE2D',
                                                          0.2,
                                                        ),
                                                      },
                                                    }}
                                                  >
                                                    <MoreVertIcon />
                                                  </Fab>
                                                )}
                                                {!permit._id && (
                                                  <Fab
                                                    onClick={() => {
                                                      removePermit(idx);
                                                    }}
                                                    color="secondary"
                                                    size="small"
                                                    sx={{
                                                      mr: 2,
                                                      '&:hover': {
                                                        backgroundColor: darken(
                                                          '#F6AE2D',
                                                          0.2,
                                                        ),
                                                      },
                                                    }}
                                                  >
                                                    <CloseIcon />
                                                  </Fab>
                                                )}
                                              </Grid>
                                            </Grid>
                                          ),
                                        )}
                                      </Paper>
                                    </Grid>
                                    <Grid
                                      item
                                      xs={3}
                                      display="flex"
                                      direction={'column'}
                                      justifyContent={'flex-end'}
                                    >
                                      <Box
                                        display="flex"
                                        justifyContent="flex-start"
                                        alignContent="center"
                                        alignItems="center"
                                      >
                                        <Fab
                                          color="primary"
                                          size="small"
                                          sx={{
                                            mr: 2,
                                            '&:hover': {
                                              backgroundColor: darken(
                                                '#F6AE2D',
                                                0.2,
                                              ),
                                            },
                                          }}
                                          onClick={() => {
                                            pushPermit({
                                              rca: '',
                                              type: '',
                                              localCouncil: false,
                                              issueDate: new Date(),
                                              expiryDate: new Date(),
                                            });
                                          }}
                                        >
                                          <AddIcon />
                                        </Fab>
                                        <Typography sx={{ color: '#F6AE2D' }}>
                                          Add Another Permit
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  </>
                                )}
                              </FieldArray>
                            </Grid>

                            <Grid container mt={8} columnSpacing={8}>
                              <Grid item xs={12}>
                                <Divider
                                  component="div"
                                  sx={{ backgroundColor: '#BD3825' }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        ))}
                      </>
                    )}
                  </FieldArray>

                  <StyledMenu
                    id="moreMenu"
                    anchorEl={anchorMoreMenu}
                    open={openMoreMenu}
                    onClose={handleCloseMenu}
                  >
                    <MenuItem onClick={onClickRemoveRow} disableRipple>
                      Delete entire row
                    </MenuItem>
                    <MenuItem onClick={onClickRemoveAll} disableRipple>
                      Remove All
                    </MenuItem>
                    <MenuItem onClick={onClickUploadAll} disableRipple>
                      Upload All
                    </MenuItem>
                    <MenuItem onClick={onClickRemovePermit} disableRipple>
                      Remove Permit
                    </MenuItem>
                    <MenuItem onClick={onClickUploadPermit} disableRipple>
                      Upload Permit
                    </MenuItem>
                    <MenuItem onClick={onClickRemoveJson} disableRipple>
                      Remove geoJSON
                    </MenuItem>
                    <MenuItem onClick={onClickUploadJson} disableRipple>
                      Upload geoJSON
                    </MenuItem>
                  </StyledMenu>
                </>
              </StyledForm>
            </>
          );
        }}
      </Formik>
    </Box>
  );
}
